import { inject } from "@angular/core";
import { Observable, catchError, map, of, switchMap } from "rxjs";
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from "@angular/router";
import { CookieService } from "ngx-cookie-service";
import { v4 as uuidv4 } from 'uuid';

import { UserService } from "../services/user.service";
import { COOKIE, CONSTANTS, SPLUNK } from "../../modules/login/util/constants";
import { PingParameter } from "../../shared/constants/constants";
import { LoginService } from "../../modules/login/services/login.service";
import { LoginResponse } from "../../modules/login/models/login.response.model";
import { LoggerService } from "../../shared/services/logger.service";
import { UserProfileResponse } from "../../shared/models/request/user-profile.response";
import { APP_CONFIG } from "../services/app-config.service";

export const inboundSsoGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean | UrlTree> | boolean | UrlTree => {

  const userService = inject(UserService);
  const loginService = inject(LoginService);
  const cookieService = inject(CookieService);
  const loggerService = inject(LoggerService);
  const router = inject(Router);

  // Check for REF parameter (both Salesforce SSO and Azure AD SSO)
  const REF_ID = route.queryParams[PingParameter.REF];

  // /inbound-sso is the Azure AD callback; /sales-force-sso is Salesforce.
  // /adlogin has no REF so it never reaches the failure branches below.
  const isAzureFlow = APP_CONFIG.ENABLE_AZURE_LOGIN === 'true' &&
    route.routeConfig?.path === 'inbound-sso';

  if (REF_ID) {
    const request = { referenceIdentifier: REF_ID };
    return loginService.authenticateUser(request)
      .pipe(
        switchMap((response: LoginResponse) => {
          if (response?.responseHeader?.transactionNotification?.status === CONSTANTS.SUCCESS) {
            // Set Session ID
            userService.setSessionID = uuidv4();
            cookieService.set(COOKIE.DP_ADM_SID, btoa(userService.sessionId),
              { domain: window.location.hostname, path: '/', sameSite: 'Strict', secure: true });

            // Update cookies and redirect to Home
            const userName = response.responseBody.username;
            const jwtToken = response.responseBody.token;
            const userId = response.responseBody.userId;
            const adAccessLevel = response.responseBody.adAccessLevel;

            // Set User Name
            cookieService.set(COOKIE.DP_ADM_USER_NAME, btoa(userName),
              { domain: window.location.hostname, path: '/', sameSite: 'Strict', secure: true });

            // Set JWT Token
            userService.setJwtToken = jwtToken;
            cookieService.set(COOKIE.DP_ADM_TOKEN, btoa(jwtToken),
              { domain: window.location.hostname, path: '/', sameSite: 'Strict', secure: true });

            // Set User Id
            userService.setUserIdentifier = userId;
            cookieService.set(COOKIE.DP_ADM_USER_ID, btoa(userId),
              { domain: window.location.hostname, path: '/', sameSite: 'Strict', secure: true });

            // Set Access Level
            if (adAccessLevel) {
              cookieService.set(COOKIE.DP_ADM_AL, btoa(adAccessLevel),
                { domain: window.location.hostname, path: '/', sameSite: 'Strict', secure: true });
            }

            // Store AD Groups if present (gated by kill switch)
            const isAzureUser = APP_CONFIG.ENABLE_AZURE_LOGIN === 'true';
            if (isAzureUser) {
              const userGroups = response.responseBody.userGroups;
              if (userGroups && userGroups.length > 0) {
                userService.setAdGroups = userGroups;
              }
              localStorage.setItem('ENABLE_AZURE_LOGIN', 'true');
            }

            // Store profile fields from login response
            if (response.responseBody.firstName) { userService.setFirstName = response.responseBody.firstName; }
            if (response.responseBody.lastName) { userService.setLastName = response.responseBody.lastName; }
            if (response.responseBody.email) { userService.setEmail = response.responseBody.email; }

            // Persist AD user profile in localStorage so it is available across tabs and after page refresh
            if (isAzureUser) {
              localStorage.setItem('AD_USER_PROFILE', JSON.stringify({
                adGroups:  response.responseBody.userGroups  || [],
                firstName: response.responseBody.firstName  || '',
                lastName:  response.responseBody.lastName   || '',
                email:     response.responseBody.email      || ''
              }));
            }
            userService.setUserName = userName;

            // Fetch full user profile before marking user as authenticated,
            // so home/header components receive a populated userProfile object.
            const profileRequest = { userName, adlogin: isAzureUser ? 'true' : 'false' };
            return userService.userProfileRequest(profileRequest).pipe(
              map((profileData: UserProfileResponse) => {
                if (profileData?.responseHeader?.transactionNotification?.status === CONSTANTS.SUCCESS) {
                  userService.setUserProfile = profileData.responseBody;
                  userService.handleSessionTimeout();
                }

                // Mark user as authenticated after profile is loaded
                userService.setAuthenticated = true;
                userService.isAuntheticated$.next(true);

                // Splunk Update
                loggerService.logEvent(
                  isAzureUser ? SPLUNK.AZURE_SSO_SUCCESS_EVENT_CD : SPLUNK.SALES_FORCE_SSO_SUCCESS_EVENT_CD,
                  isAzureUser ? SPLUNK.AZURE_SSO_SUCCESS_EVENT_DESC : SPLUNK.SALES_FORCE_SSO_SUCCESS_EVENT_DESC,
                  isAzureUser ? SPLUNK.AZURE_SSO_SUCCESS_ID : SPLUNK.SALES_FORCE_SSO_SUCCESS_ID,
                  '',
                  {
                    adminUserId: userId,
                    adminUserName: userName
                  },
                  CONSTANTS.SUCCESS
                );

                return resolveRedirectTarget(route, router, cookieService);
              }),
              catchError(() => {
                // Profile fetch failed — still allow login, just mark authenticated without profile
                userService.setAuthenticated = true;
                userService.isAuntheticated$.next(true);
                return of(resolveRedirectTarget(route, router, cookieService));
              })
            );
          } else {
            // Check if there are any error messages
            if (response?.responseHeader?.transactionNotification?.remarks?.messages?.length) {
              // Get the error message
              const errorMessage = response.responseHeader.transactionNotification.remarks.messages[0];
              // Check error code and set appropriate error message
              if (errorMessage.code === CONSTANTS.UNAUTHORIZED_CODE || errorMessage.code === CONSTANTS.USER_NOT_FOUND_CODE) {
                return of(navigateToLogin(userService, router, loggerService, CONSTANTS.INVALID_CREDENTIALS, isAzureFlow));
              } else if (errorMessage.code === CONSTANTS.ACCOUNT_INACTIVE_CODE) {
                return of(navigateToLogin(userService, router, loggerService, CONSTANTS.INVALID_ACCOUNT_AZURE, isAzureFlow));
              } else {
                return of(navigateToLogin(userService, router, loggerService, CONSTANTS.LOGIN_EXECEPTION, isAzureFlow));
              }
            } else {
              // If no error messages, set LOGIN_EXCEPTION as error message
              return of(navigateToLogin(userService, router, loggerService, CONSTANTS.LOGIN_EXECEPTION, isAzureFlow));
            }
          }
        }), catchError(() => {
          return of(navigateToLogin(userService, router, loggerService, CONSTANTS.LOGIN_EXECEPTION, isAzureFlow));
        })
      );
  } else {
    // If no REF_ID, check if this is /adlogin route - redirect to Azure SSO (gated by kill switch)
    if (state.url.includes('/adlogin')) {
      if (APP_CONFIG.ENABLE_AZURE_LOGIN === 'true') {
        window.location.href = APP_CONFIG.DPADMIN_AZURE_SSO_URL;
        return false;
      }
      return router.createUrlTree(['login']);
    }
    return navigateToLogin(userService, router, loggerService, CONSTANTS.LOGIN_EXECEPTION, isAzureFlow);
  }

};

const resolveRedirectTarget = (
  route: ActivatedRouteSnapshot,
  router: Router,
  cookieService: CookieService
): boolean | UrlTree => {
  const targetURL = route.queryParams['targetURL'];
  if (targetURL) {
    return router.parseUrl(targetURL);
  }

  const benefitLookupUrl = cookieService.get(COOKIE.BENEFIT_LOOKUP_URL);
  cookieService.delete(COOKIE.BENEFIT_LOOKUP_URL, '/', window.location.hostname, true, 'Strict');

  if ((benefitLookupUrl !== null && benefitLookupUrl !== "") && benefitLookupUrl !== undefined) {
    router.navigateByUrl(benefitLookupUrl);
    return true;
  }
  router.navigate([CONSTANTS.HOME_URL], { replaceUrl: true });
  return false;
};

const navigateToLogin = (
  userService: UserService,
  router: Router,
  loggerService: LoggerService,
  errorMessage: string,
  isAzureFlow: boolean
): boolean | UrlTree => {
  const isAzureLogin = isAzureFlow;

  loggerService.logEvent(
    isAzureLogin ? SPLUNK.AZURE_SSO_FAILURE_EVENT_CD : SPLUNK.SALES_FORCE_SSO_FAILURE_EVENT_CD,
    isAzureLogin ? SPLUNK.AZURE_SSO_FAILURE_EVENT_DESC : SPLUNK.SALES_FORCE_SSO_FAILURE_EVENT_DESC,
    isAzureLogin ? SPLUNK.AZURE_SSO_FAILURE_ID : SPLUNK.SALES_FORCE_SSO_FAILURE_ID,
    '',
    {
      adminUserName: isAzureLogin ? CONSTANTS.AZURE_SSO_USER : CONSTANTS.SALESFORCE_SSO_USER
    },
    CONSTANTS.FAILURE
  );
  userService.ssoLoginErrorMessage$.next(errorMessage);
  userService.logoutUser();
  return router.createUrlTree([isAzureLogin ? 'landing' : 'login']);
}